// src/components/auth/ProtectedRoute.jsx
import { Navigate, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { PageOverlayLoading } from "../Loading/Spinner.jsx";

/**
 * Reittivartija:
 * - Odottaa auth-bootstrapin valmistumisen ennen redirect-päätöstä
 * - Hard refresh (F5 / Ctrl+Shift+R) ei heitä käyttäjää ulos kesken sessiotarkistuksen
 * - Ohjaa /kirjaudu?next=... vain, kun auth-tila on varmasti ratkaistu
 * - Säilyttää nykyisen dashboard-URL:n next-parametrissa
 */
export default function ProtectedRoute({ children }) {
  const location = useLocation();
  const auth = useAuth?.() || {};

  // AuthContext käyttää tällä hetkellä loading-arvoa bootstrapin aikana.
  // Tuetaan samalla mahdollisia vanhoja alias-nimiä.
  const rawChecking =
    auth.checking ??
    auth.checkingSession ??
    auth.loading ??
    false;

  // Authed? Hyväksy joko isAuthenticated-boolean tai user-olio.
  const isAuthed = useMemo(() => {
    if (auth.user) return true;

    if (typeof auth.isAuthenticated === "boolean") {
      return auth.isAuthenticated;
    }

    return false;
  }, [auth.isAuthenticated, auth.user]);

  /**
   * TÄRKEÄ:
   *
   * Hard refreshissa AuthContext käynnistyy hetkellisesti:
   *
   * user = undefined
   * loading = true
   * isAuthenticated = false
   *
   * isAuthenticated=false ei silloin vielä tarkoita uloskirjautunutta,
   * koska sessiotarkistus on edelleen käynnissä.
   *
   * Odotetaan siis aina bootstrapin valmistumista, jos käyttäjää
   * ei ole vielä saatu takaisin.
   */
  const shouldBlockOnChecking =
    !!rawChecking &&
    !auth.user;

  if (shouldBlockOnChecking) {
    return <PageOverlayLoading label="Avataan hallintaa…" />;
  }

  // Auth-tarkistus on valmis ja käyttäjää ei löytynyt.
  // Vasta NYT ohjataan kirjautumiseen.
  if (!isAuthed) {
    const next =
      `${location.pathname}` +
      `${location.search || ""}` +
      `${location.hash || ""}`;

    return (
      <Navigate
        to={`/kirjaudu?next=${encodeURIComponent(next)}`}
        replace
      />
    );
  }

  return children;
}